import { Row } from 'components/Layout';
import TransactionAllocator from 'components/TransactionAllocator';
import { type Transaction } from 'components/TransactionAllocator';
import { useLayoutContext } from 'contexts/LayoutContext';
import { ActionTransactionSubTypes } from 'further-types/lp/action';
import { CurrencyRateDetails } from 'further-types/lp/transaction';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { numberToCurrencyString } from 'further-ui/utils';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import { ApiResponses } from 'lib/trpcClient';
import { useEffect, useState } from 'react';
import CashHistory from './CashHistory';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { styled } from '@mui/material/styles';
import { Column } from 'further-ui/components/Layout';

const ExpandedRowContainer = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  borderTop: `1px solid ${theme.palette.grey[300]}`,
}));

type Props = {
  vehicleId: string;
  data: ApiResponses['unrealisedCashFxGainOrLoss']['list'] | undefined;
  isFetching: boolean;
  onCurrenciesLoaded: (
    currencies: Array<[CurrencyCode, CurrencyRateDetails]>,
  ) => void;
  transactionChanges: Map<string, Array<Transaction>>;
  onTransactionChange: (
    rowId: string,
    transactions: Array<Transaction>,
  ) => void;
  onViewRecord?: (recordId: string) => void;
};

const Cash: React.FC<Props> = ({
  vehicleId,
  data,
  isFetching,
  onCurrenciesLoaded,
  transactionChanges,
  onTransactionChange,
  onViewRecord,
}) => {
  const { vehicle } = useLayoutContext();
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);

  const { transactionTypeOptions, isLoading: transactionTypesLoading } =
    useTransactionTypeSelectorOptions();

  useEffect(() => {
    if (!data?.currencyRates) return;
    onCurrenciesLoaded(data.currencyRates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.currencyRates]);

  if (!vehicle) {
    return null;
  }

  const rows =
    data?.results?.map((row) => ({
      ...row,
      baseCurrency: vehicle.baseCurrency,
    })) ?? [];

  const buildAllocatorTransactions = (row: (typeof rows)[number]) => {
    const transactions = transactionChanges.get(row.id);

    if (transactions) {
      return transactions;
    }

    return row.transactions.map((transaction) => ({
      ...transaction,
      transactionCurrency: row.currencyCode,
      transactionSubType: ActionTransactionSubTypes.CreditDebitPair,
    }));
  };

  const columns: Array<TableColumn<(typeof rows)[number]>> = [
    {
      key: 'bankName',
      label: 'Bank name',
      disableSorting: true,
    },
    {
      key: 'accountName',
      label: 'Account name',
      disableSorting: true,
    },
    {
      key: 'balanceTcy',
      label: 'Balance (TCY)',
      render: (row) =>
        numberToCurrencyString(row.balanceTcy, {
          fallback: '-',
          currency: row.currencyCode,
        }),
      disableSorting: true,
    },
    {
      key: 'originalValueLcy',
      label: 'Original value (LCY)',
      render: (row) =>
        numberToCurrencyString(row.calculationBreakdown?.originalValueLcy, {
          fallback: '-',
          currency: row.baseCurrency,
        }),
      disableSorting: true,
    },
    {
      key: 'runningTotalUnrealisedLcy',
      label: 'Running total unrealised',
      render: (row) =>
        numberToCurrencyString(
          row.calculationBreakdown?.runningTotalUnrealisedLcy,
          {
            fallback: '-',
            currency: row.baseCurrency,
            negativeInBrackets: true,
          },
        ),
      disableSorting: true,
    },
    {
      key: 'currentValueLcy',
      label: 'Current value (LCY)',
      render: (row) =>
        numberToCurrencyString(row.calculationBreakdown?.currentValueLcy, {
          fallback: '-',
          currency: row.baseCurrency,
        }),
      disableSorting: true,
    },
    {
      key: 'unrealisedCashFxGainValueLcy',
      label: 'Unrealised gain/loss (LCY)',
      render: (row) =>
        numberToCurrencyString(row.unrealisedCashFxGainValueLcy, {
          fallback: '-',
          currency: row.baseCurrency,
          negativeInBrackets: true,
        }),
      disableSorting: true,
    },
  ];

  return (
    <Column spacing="lg">
      <Table
        columns={columns}
        rows={rows}
        isLoading={isFetching}
        isExpandableRow={() => true}
        isExpandedRow={(row) => expandedRowId === row.id}
        onRowExpand={(row) =>
          setExpandedRowId((prev) => (prev === row.id ? null : row.id))
        }
        getExpandedContent={(row) => (
          <ExpandedRowContainer>
            <Row fullWidth>
              {!transactionTypesLoading && (
                <TransactionAllocator
                  transactionTypeOptions={transactionTypeOptions}
                  transactions={buildAllocatorTransactions(row)}
                  onChange={(newTransactions) => {
                    onTransactionChange(row.id ?? '', newTransactions);
                  }}
                  transactionCurrency={row.currencyCode}
                  vehicle={vehicle}
                  fxRate={row.currencyRate}
                />
              )}
            </Row>
          </ExpandedRowContainer>
        )}
      />
      <CashHistory vehicleId={vehicleId} onViewRecord={onViewRecord} />
    </Column>
  );
};

export default Cash;
